/**
 * applyModeUrlCoercion — mode-URL coercion as a FIRST-IMPORT side effect.
 *
 * WHY THIS IS ITS OWN MODULE (2026-06-12): this block used to live in
 * main.tsx's body, which runs AFTER every import of main.tsx is evaluated —
 * including (transitively) devConfig.ts, which resolves the active preset
 * from the URL at ITS module load. So on any same-tab navigation to a mode
 * route, DevConfig read the PRE-coercion URL (no ?preset → LIVE-LOBBY) and
 * the coerced params arrived too late. The bug was masked on each tab's
 * FIRST visit by the ?reset=1 wipe+reload (the second boot read the already-
 * coerced URL); every SUBSEQUENT same-tab entry — e.g. the editor Test
 * surface's "← Back to Editor" pill — booted the wrong scene (live-lobby,
 * no editor overlay).
 *
 * main.tsx imports this module FIRST, and the import closure here is
 * dependency-free (plain config consts + coerceModeUrl), so the URL is
 * canonical before ANY other module — devConfig included — reads it.
 *
 * Knob surfaces live in src/config/{brawl,pve,…}/. Edit those files to
 * change what each URL does, never edit this block.
 */

import { BRAWL_CONFIG } from '../brawl/BrawlConfig';
import { ARENA_CONFIG } from '../arena/ArenaConfig';
import { PVE_CONFIG } from '../pve/PvEConfig';
import { ADVENTURE_CONFIG } from '../adventure/AdventureConfig';
import { ABILITY_GROUNDS_CONFIG } from '../abilitygrounds/AbilityGroundsConfig';
import { LOBBY_CONFIG } from '../lobby/LobbyConfig';
import { GM_RACE_CONFIG } from '../gmrace/GmRaceConfig';
import { DEBUG_CONFIG } from '../debug/DebugConfig';
import { COMBAT_LAB_CONFIG } from '../combatlab/CombatLabConfig';
import { EDITOR_CONFIG } from '../editor/EditorConfig';
import { BIOMES_CONFIG } from '../biomes/BiomesConfig';
import { INTRO_CONFIG } from '../intro/IntroConfig';
import { RESUME_CONFIG } from '../resume/ResumeConfig';
import { SUNKEN_CONFIG } from '../sunken/SunkenConfig';
import { SCREENSAVER_CONFIG } from '../screensaver/ScreensaverConfig';
import { SCREENSAVER_BIOME_CYCLE } from '../screensaver/screensaverCycle';
import {
  HOLLOW_KEEPER_QA_CONFIG,
  HOLLOW_KEEPER_QA_MAP_ID,
  HOLLOW_KEEPER_QA_ROUTE,
} from '../qa/HollowKeeperQaConfig';
import {
  MOBILE_QA_CONFIG,
  MOBILE_QA_MAP_ID,
  MOBILE_QA_ROUTE,
} from '../qa/MobileQaConfig';
import { coerceModeUrl } from './coerceModeUrl';
import { bootPathname, bootSearch, bootSearchParams } from './bootUrl';

try {
  // Consolidation: /abilitytest is folded into the richer /abilitygrounds.
  // Rewrite the path BEFORE the coercion calls below — so old /abilitytest
  // links land in the Grounds AND still get their params coerced (a
  // React-Router redirect would fire AFTER module-load coercion and miss it).
  if (typeof window !== 'undefined' && bootPathname() === '/abilitytest') {
    try { window.history.replaceState(null, '', '/abilitygrounds' + bootSearch()); } catch { /* best-effort */ }
  }

  // These are deterministic dev-character test surfaces — DJ should land in
  // the dev character every time, never the AuthScreen. The per-URL ?reset=1
  // marker fires only ONCE per browser session, so a stale
  // `helix_twitch_recent` localStorage hint set after the wipe would defer
  // useAuth's dev shortcut and strand DJ on AuthScreen. Wipe the hint
  // synchronously on every visit so initialSkipLogin always wins here.
  // /gmrace is deliberately NOT in this list — it is a Twitch-identity
  // surface (forceLogin) and must keep the returning-user session hint.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    const path = bootPathname();
    if (path === '/brawl' || path === '/pve' || path === '/adventure' || path === '/abilitygrounds' || path === '/lobby' || path === '/debug' || path === '/combatlab' || path === '/editor' || path === '/biomes' || path === '/intro' || path === '/resume' || path === '/sunken' || path === '/screensaver' || path === HOLLOW_KEEPER_QA_ROUTE || path === MOBILE_QA_ROUTE) {
      try { localStorage.removeItem('helix_twitch_recent'); } catch { /* best-effort */ }
    }

    // Biome-tour marker — the session-scoped "Return to Biome Nexus" button
    // (BiomeNexusReturn) keys off this. Set it when entering the /biomes
    // showcase; clear it when entering any OTHER mode so the ungated hop-back
    // never leaks into normal progression (where reaching a biome must not
    // expose a level-gate-skipping warp to the Nexus). Cross-map warps land on
    // bare `/?map=…` (no mode coercion), so the flag persists across the tour.
    try {
      if (path === '/biomes') {
        sessionStorage.setItem('helix_biome_tour', '1');
      } else if (path !== '/') {
        sessionStorage.removeItem('helix_biome_tour');
      }
    } catch { /* best-effort */ }

    // General Peaceful Mode flag (VQA/dev) — independent of the biome tour and
    // NOT cleared on mode changes, so a single `?peaceful=1` keeps every
    // subsequent map-to-map VQA hop peaceful for the whole session. `?peaceful=0`
    // clears it. NetworkManager reads `helix_peaceful` and the server grants
    // Peaceful Mode on ANY map (symmetric no-damage; can't be abused for PvP).
    try {
      const sp = bootSearchParams();
      const p = sp.get('peaceful');
      if (p === '1') sessionStorage.setItem('helix_peaceful', '1');
      else if (p === '0') sessionStorage.removeItem('helix_peaceful');
      // /sunken is a pure atmosphere tour (The Drowned Cathedral) — force Peaceful
      // so soaking in the whales never gets you killed (unless explicitly ?peaceful=0).
      if (path === '/sunken' && p !== '0') sessionStorage.setItem('helix_peaceful', '1');
      // /screensaver is the stream-AFK biome tour — always Peaceful so the
      // detached-camera tourist never dies (unless explicitly ?peaceful=0).
      if (path === '/screensaver' && p !== '0') sessionStorage.setItem('helix_peaceful', '1');
    } catch { /* best-effort */ }
  }

  // Class is intentionally NOT injected by any of these — PreGameFlow
  // auto-picks from `?class=X`, so setting a default would skip the roster
  // entirely. HomeModePicker navigates without a class param so DJ sees the
  // picker; ClassSwitcherTopBar's mid-game `?class=X&pickCharacter=0` and
  // direct deep-links still work as expected.
  coerceModeUrl({ path: '/brawl',       mapId: 'brawl-arena',        resetMarkerKey: 'helix_brawl_reset_v1',       config: BRAWL_CONFIG });
  coerceModeUrl({ path: '/arena',       mapId: 'arena-multi-level',  resetMarkerKey: 'helix_arena_reset_v1',       config: ARENA_CONFIG });
  coerceModeUrl({ path: '/pve',         mapId: 'pve-arena',          resetMarkerKey: 'helix_pve_reset_v1',         config: PVE_CONFIG });
  coerceModeUrl({ path: '/adventure',   mapId: 'tidewend',           resetMarkerKey: 'helix_adventure_reset_v1',   config: ADVENTURE_CONFIG });
  coerceModeUrl({ path: '/abilitygrounds', mapId: 'ability-grounds-arena', resetMarkerKey: 'helix_abilitygrounds_reset_v1', config: ABILITY_GROUNDS_CONFIG });
  coerceModeUrl({ path: '/lobby',       mapId: 'dj-live-lobby',      resetMarkerKey: 'helix_lobby_reset_v1',       config: LOBBY_CONFIG });
  // /gmrace — one-click entry to the GM Race lobby. Auto-creates a race event
  // on room load; players auto-join. DJ (admin) sees a "Start Race" button.
  // forceLogin: public hosts gate through the Twitch AuthScreen (racers carry
  // their Twitch names; DJ's admin tier rides his Twitch session).
  coerceModeUrl({ path: '/gmrace',      mapId: 'gm_race_lobby',      resetMarkerKey: 'helix_gmrace_reset_v1',      config: GM_RACE_CONFIG });
  coerceModeUrl({ path: '/debug',       mapId: 'debug-lobby',        resetMarkerKey: 'helix_debug_reset_v1',       config: DEBUG_CONFIG });
  // /combatlab — the measured combat/VFX arena. Fixed anchors, ruler ticks in
  // TRUE combat distance, inert dummies. Where cone/arc/VFX readings are taken.
  coerceModeUrl({ path: '/combatlab',   mapId: 'combat-lab',         resetMarkerKey: 'helix_combatlab_reset_v1',   config: COMBAT_LAB_CONFIG });
  coerceModeUrl({ path: '/biomes',      mapId: 'biome_nexus',        resetMarkerKey: 'helix_biomes_reset_v1',      config: BIOMES_CONFIG });
  // /intro — the first-time-character test surface (spawn fresh in tomb_waking,
  // walk the tomb → village → first_field on-ramp chain — the opening as a
  // brand-new player sees it). Exit teleporter currently lands on pig_beach
  // as a legacy fallback until village_arrival + first_field ship.
  coerceModeUrl({ path: '/intro',       mapId: 'tomb_waking',        resetMarkerKey: 'helix_intro_reset_v1',       config: INTRO_CONFIG });
  // /resume — the saved-character roster (resume a hero's full persisted state, or
  // abandon it). Calm Tidewend hub so resumed heroes don't drop into combat.
  coerceModeUrl({ path: '/resume',      mapId: 'tidewend',           resetMarkerKey: 'helix_resume_reset_v1',      config: RESUME_CONFIG });
  // /sunken — The Drowned Cathedral, the sunkenBiome (whale-drift) showcase. Peaceful.
  coerceModeUrl({ path: '/sunken',      mapId: 'sunken_drift',       resetMarkerKey: 'helix_sunken_reset_v1',      config: SUNKEN_CONFIG });
  // /screensaver — stream-AFK biome tour. Boots into the first cycle biome; the
  // ScreensaverSystem (registered in screensaver mode) drives camera + cycling.
  coerceModeUrl({ path: '/screensaver', mapId: SCREENSAVER_BIOME_CYCLE[0].mapId, resetMarkerKey: 'helix_screensaver_reset_v1', config: SCREENSAVER_CONFIG });
  // Local memory-mode encounter shortcut. The URL only identifies the
  // scenario; DebugHandler owns the guarded, non-persisted preparation.
  coerceModeUrl({
    path: HOLLOW_KEEPER_QA_ROUTE,
    mapId: HOLLOW_KEEPER_QA_MAP_ID,
    resetMarkerKey: 'helix_hollow_keeper_qa_reset_v1',
    config: HOLLOW_KEEPER_QA_CONFIG,
  });
  // /editor — one-click into the in-game level editor. EDITOR_CONFIG.editorOn
  // → ?editorOn=1 opens the EditorOverlay on boot; preset 'editor' flips
  // DevConfig.systems.editor on. Together they satisfy GameUIOverlay's
  // `DevConfig.systems.editor && isEditorMode` mount gate.
  coerceModeUrl({ path: '/editor',      mapId: EDITOR_CONFIG.mapId,  resetMarkerKey: 'helix_editor_reset_v1',      config: EDITOR_CONFIG });
  // One-click mobile touch QA shortcut. Boots first_field with forced touch
  // controls, Dev abilities, and noclip.
  coerceModeUrl({
    path: MOBILE_QA_ROUTE,
    mapId: MOBILE_QA_MAP_ID,
    resetMarkerKey: 'helix_mobile_qa_reset_v1',
    config: MOBILE_QA_CONFIG,
  });
} catch {
  // sessionStorage / replaceState failures — best-effort, fall through.
}
